import * as d3 from "d3";
import { scaleLinear } from "d3-scale";
import React, { useRef, useEffect } from "react";

export default function IndividualZimmerBlock(props) {
    const {
        data,
        mapType,
        tableCoordinates,
        selectedTt, 
        selectedPier,
        current,
        view, 
        handleClickBay,
        handleClickPier
    } = props;

    const containerRef = useRef();

    useEffect(() => {
        if (containerRef.current && data && tableCoordinates?.table_coordinates?.length > 0) {
            renderMap();
        }
        // eslint-disable-next-line
    }, [data, current, view, mapType, tableCoordinates]);


    const renderMap = () => {
        if (containerRef.current) {
            d3.select(containerRef.current).selectAll("*").remove();
        }

        let xTableCoordinates = [];
        let yTableCoordinates = [];
        if (tableCoordinates?.table_coordinates && tableCoordinates.table_coordinates.length !== 0) {
            for (let i of tableCoordinates.table_coordinates) {
                xTableCoordinates.push(i["x"]);
                yTableCoordinates.push(i["y"]);
            }
        }

        if (xTableCoordinates.length === 0 || yTableCoordinates.length === 0) return;

        let xScale = scaleLinear()
            .domain([Math.min(...xTableCoordinates), Math.max(...xTableCoordinates)])
            .range([0, 400]);

        let yScale = scaleLinear()
            .domain([Math.max(...yTableCoordinates), Math.min(...yTableCoordinates)])
            .range([0, 150]);

        renderTable(tableCoordinates, xScale, yScale);
        renderBays(xScale, yScale);
        renderPiers(xScale, yScale);
    };

    const renderTable = (tableData, xScale, yScale) => {
        if (!tableData) return;
        const ttData = tableData.table_coordinates;
        const ttColorCode = tableData.color_code || "255-191-0";
        const ttHatch = tableData.hatch;

        if (ttData && ttData.length >= 4) {
            d3.select(containerRef.current)
                .append("path")
                .attr("class", "testing-border")
                .attr("d", `M${xScale(ttData[0].x)} ${yScale(ttData[0].y)} L${xScale(ttData[1].x)} ${yScale(ttData[1].y)} L${xScale(ttData[2].x)} ${yScale(ttData[2].y)} L${xScale(ttData[3].x)} ${yScale(ttData[3].y)} Z`)
                .attr("stroke", `rgb(${ttColorCode.split("-").join(",")})`)
                .attr("stroke-width", "1px")
                .attr("fill", "white");

            if (ttHatch) {
                d3.select(containerRef.current)
                    .append("line")
                    .attr("class", "testing-border-diag")
                    .attr("x1", xScale(ttData[0].x))
                    .attr("y1", yScale(ttData[0].y))
                    .attr("x2", xScale(ttData[2].x))
                    .attr("y2", yScale(ttData[2].y))
                    .attr("stroke", `rgb(${ttColorCode.split("-").join(",")})`);
            }
        }
    };

    const renderBays = (xScale, yScale) => {
        if (!data?.tt) return;

        data.tt.forEach((data) => {
            const bayId = parseInt(data.id);
            const ttData = data?.tt_window_zimmerman;
            const isSelected = mapType === 0 || selectedTt?.[0]?.id === bayId;

            d3.select(containerRef.current)
                .append("path")
                .attr("class", `tableBays ${mapType === 1 && current !== "piers" ? "cursor-pointer" : ""}`)
                .attr("d", `M${xScale(ttData[0]?.latitude)} ${yScale(ttData[0]?.longitude)}
                    L${xScale(ttData[1]?.latitude)} ${yScale(ttData[1]?.longitude)}
                    L${xScale(ttData[3]?.latitude)} ${yScale(ttData[3]?.longitude)}
                    L${xScale(ttData[5]?.latitude)} ${yScale(ttData[5]?.longitude)}
                    L${xScale(ttData[4]?.latitude)} ${yScale(ttData[4]?.longitude)}
                    L${xScale(ttData[2]?.latitude)} ${yScale(ttData[2]?.longitude)} Z`)
                .attr("stroke", "black")
                .attr("stroke-width", "1px")
                .attr("fill", isSelected && current !== "piers" ? "yellow" : "white")
                .style("cursor", "pointer")
                .on("click", mapType === 1 && current !== "piers" ? () => handleClickBay(data) : null);

            const bayNumber = (data.tt_part_label || data.part_number || "").replace(/\D/g, '');

            if (bayNumber) {
                const centerX = (xScale(ttData[0]?.latitude) + xScale(ttData[3]?.latitude)) / 2;
                const centerY = (yScale(ttData[0]?.longitude) + yScale(ttData[3]?.longitude)) / 2;

                d3.select(containerRef.current)
                    .append("text")
                    .attr("x", centerX)
                    .attr("y", centerY)
                    .attr("text-anchor", "middle")
                    .attr("dominant-baseline", "middle")
                    .attr("fill", "black")
                    .style("font-size", "10px")
                    .text(bayNumber)
                    .on("click", mapType === 1 && current !== "piers" ? () => handleClickBay(data) : null);
                    // .on("click", mapType === 1 && current !== "piers" ? () => handleClickBay(data) : null);
            }
        });
    };

    const renderPiers = (xScale, yScale) => {
        if (!data?.piers) return;

        data.piers.forEach((data) => {
            const pierId = parseInt(data.id);
            const isSelected = view === "Individual" ? selectedPier?.[0]?.id === pierId : current === "piers" ? true : false;

            if (isSelected) {
                d3.select(containerRef.current)
                    .append("circle")
                    .attr("class", mapType === 1 && current === "piers" ? "cursor-pointer" : "")
                    .attr("class", mapType === 1 && current === "piers" ? "cursor-pointer" : "")
                    .attr("cx", xScale(data.latitude))
                    .attr("cy", yScale(data.longitude))
                    .attr("r", 12)
                    .attr("fill", "yellow")
                    .attr("stroke", "black")
                    .attr("stroke-width", "1px")
                    .on("click", mapType === 1 && current === "piers" ? () => handleClickPier(data) : null);
                    // .on("click", mapType === 1 && current === "piers" ? () => handleClickPier(data) : null);
            }

            d3.select(containerRef.current)
                .append("text")
                .attr("x", xScale(data.latitude))
                .attr("y", yScale(data.longitude))
                .attr("text-anchor", "middle")
                .attr("dominant-baseline", "middle")
                .attr("fill", "black")
                .style("font-size", "13px")
                .style("font-weight", "700")
                .style("cursor", "pointer")
                .text(data?.pier_part_order || data?.part_number || "")
                .on("click", mapType === 1 && current === "piers" ? () => handleClickPier(data) : null);
        });
    };

    return (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", padding: "10px 0" }}>
            <div style={{ width: "100%", maxWidth: "400px", height: "150px" }}>
                <svg
                    ref={containerRef}
                    style={{ width: "100%", height: "100%", margin: "auto" }}
                    viewBox="0 0 400 150"
                    preserveAspectRatio="xMidYMid meet"
                />
            </div>
        </div>
    );
}
